import {
  MULTIPLICATION_CATALOG,
  TABLE_MASTERY_STAGES,
  buildFluencyBaseline,
  buildTableHint,
  evaluateTableFactMastery,
  selectNextTableFact,
  validateMultiplicationAttempt,
  type MultiplicationAttempt,
  type MultiplicationFact,
  type MultiplicationFactId,
  type TableMasteryStage,
} from "../../../packages/tafel-core/src/index.ts";

const PROFILE_ID = "anthony";
const STORAGE_KEY = "tafelheldFluencyV1";
const SESSION_LENGTH = 15;
const MAX_ATTEMPTS_PER_FACT = 50;
const app = document.querySelector<HTMLDivElement>("#app")!;

interface SessionRecord {
  readonly id: string;
  readonly startedAt: string;
  readonly endedAt: string;
  readonly questionCount: number;
  readonly firstTryCorrect: number;
  readonly helpCount: number;
  readonly stoppedEarly: boolean;
}

interface StoredFluencyState {
  readonly schemaVersion: 1;
  readonly attempts: readonly MultiplicationAttempt[];
  readonly sessions: readonly SessionRecord[];
}

interface RetryItem {
  readonly factId: MultiplicationFactId;
  readonly availableAfter: number;
}

interface ActiveSession {
  readonly id: string;
  readonly startedAt: string;
  completed: number;
  firstTryCorrect: number;
  helpCount: number;
  recentFactIds: MultiplicationFactId[];
  sessionFactIds: MultiplicationFactId[];
  retryQueue: RetryItem[];
  startStages: ReadonlyMap<MultiplicationFactId, TableMasteryStage>;
}

interface ActiveQuestion {
  readonly presentationId: string;
  readonly fact: MultiplicationFact;
  readonly originalPresentedAt: string;
  attemptOrdinal: number;
  entry: string;
  firstInputAt: string | null;
  editCount: number;
  supportUsed: boolean;
  helpRequested: boolean;
  locked: boolean;
}

let state = loadState();
let activeSession: ActiveSession | null = null;
let activeQuestion: ActiveQuestion | null = null;
let currentScreen: "HOME" | "PRACTICE" | "SUMMARY" = "HOME";
let storageAvailable = canUseStorage();

function createId(prefix: string): string {
  const suffix = typeof crypto.randomUUID === "function"
    ? crypto.randomUUID()
    : `${Date.now()}-${Math.random().toString(36).slice(2)}`;
  return `${prefix}-${suffix}`;
}

function emptyState(): StoredFluencyState {
  return { schemaVersion: 1, attempts: [], sessions: [] };
}

function canUseStorage(): boolean {
  try {
    const key = `${STORAGE_KEY}.test`;
    localStorage.setItem(key, "1");
    localStorage.removeItem(key);
    return true;
  } catch {
    return false;
  }
}

function loadState(): StoredFluencyState {
  try {
    const raw = localStorage.getItem(STORAGE_KEY);
    if (!raw) return emptyState();
    const parsed = JSON.parse(raw) as Partial<StoredFluencyState>;
    if (parsed.schemaVersion !== 1 || !Array.isArray(parsed.attempts)) return emptyState();
    return {
      schemaVersion: 1,
      attempts: parsed.attempts,
      sessions: Array.isArray(parsed.sessions) ? parsed.sessions : [],
    };
  } catch {
    return emptyState();
  }
}

function pruneAttempts(attempts: readonly MultiplicationAttempt[]): MultiplicationAttempt[] {
  const perFact = new Map<MultiplicationFactId, MultiplicationAttempt[]>();
  attempts.forEach((attempt) => {
    const factAttempts = perFact.get(attempt.factId) ?? [];
    factAttempts.push(attempt);
    perFact.set(attempt.factId, factAttempts);
  });
  return [...perFact.values()]
    .flatMap((factAttempts) => factAttempts.slice(-MAX_ATTEMPTS_PER_FACT))
    .sort((a, b) => Date.parse(a.submittedAt) - Date.parse(b.submittedAt));
}

function persistState(): void {
  if (!storageAvailable) return;
  try {
    localStorage.setItem(STORAGE_KEY, JSON.stringify(state));
  } catch {
    storageAvailable = false;
  }
}

function appendAttempt(attempt: MultiplicationAttempt): void {
  const issues = validateMultiplicationAttempt(attempt);
  if (issues.length > 0) throw new Error(issues.map((issue) => issue.message).join(" "));
  state = {
    ...state,
    attempts: pruneAttempts([...state.attempts, attempt]),
  };
  persistState();
}

function masteryRank(stage: TableMasteryStage): number {
  return TABLE_MASTERY_STAGES.indexOf(stage);
}

function masterySnapshot(now = new Date().toISOString()): ReadonlyMap<MultiplicationFactId, ReturnType<typeof evaluateTableFactMastery>> {
  const baseline = buildFluencyBaseline(state.attempts);
  return new Map(MULTIPLICATION_CATALOG.facts.map((fact) => [
    fact.id,
    evaluateTableFactMastery(fact.id, state.attempts, now, baseline),
  ]));
}

function renderHome(): void {
  currentScreen = "HOME";
  activeQuestion = null;
  const snapshot = masterySnapshot();
  const seen = [...snapshot.values()].filter((item) => item.currentStage !== "UNSEEN").length;
  const building = [...snapshot.values()].filter(
    (item) => item.currentStage !== "UNSEEN"
      && masteryRank(item.currentStage) < masteryRank("FLUENT"),
  ).length;
  const fluent = [...snapshot.values()].filter(
    (item) => masteryRank(item.currentStage) >= masteryRank("FLUENT"),
  ).length;
  const due = [...snapshot.values()].filter(
    (item) => item.retentionState === "DUE" || item.retentionState === "REFRESH_DUE",
  ).length;

  const tableRows = Array.from({ length: 10 }, (_, index) => index + 1).map((table) => {
    const facts = MULTIPLICATION_CATALOG.facts.filter((fact) => fact.left === table);
    const fluentCount = facts.filter((fact) => {
      const stage = snapshot.get(fact.id)?.currentStage ?? "UNSEEN";
      return masteryRank(stage) >= masteryRank("FLUENT");
    }).length;
    const buildingCount = facts.filter((fact) => {
      const stage = snapshot.get(fact.id)?.currentStage ?? "UNSEEN";
      return stage !== "UNSEEN" && masteryRank(stage) < masteryRank("FLUENT");
    }).length;
    const activeCount = fluentCount + buildingCount;
    return `
      <div class="table-row">
        <span class="table-label">Tafel ${table}</span>
        <div class="table-track" aria-label="${buildingCount} van 10 in opbouw en ${fluentCount} van 10 vlot">
          <div class="table-fill table-fill-building" style="width:${activeCount * 10}%"></div>
          <div class="table-fill table-fill-fluent" style="width:${fluentCount * 10}%"></div>
        </div>
        <span class="table-count"><strong>${fluentCount} vlot</strong><small>${buildingCount} oefenen</small></span>
      </div>`;
  }).join("");

  app.innerHTML = `
    <section class="card home-card">
      <p class="eyebrow">Jouw volgende training</p>
      <h1>Rustig denken.<br />Steeds sneller weten.</h1>
      <p class="intro">Je krijgt 15 sommen. Moeilijke sommen komen slim terug, zonder aftellende klok.</p>
      <div class="stats">
        <div class="stat"><strong>${seen}</strong><span>gezien</span></div>
        <div class="stat"><strong>${building}</strong><span>in opbouw</span></div>
        <div class="stat"><strong>${fluent}</strong><span>vlot</span></div>
      </div>
      <button class="primary-btn" id="startSession" type="button">Start 15 sommen</button>
      <p class="session-note">Meestal ongeveer 3 tot 5 minuten</p>
      ${due > 0 ? `<p class="refresh-note">↻ ${due} ${due === 1 ? "som staat" : "sommen staan"} klaar om op te frissen.</p>` : ""}
      ${storageAvailable ? "" : '<p class="storage-warning">Voortgang kan in deze browser niet worden bewaard.</p>'}
    </section>
    <section class="card table-overview">
      <h2 class="section-title">Tafels 1 tot en met 10</h2>
      <div class="table-legend" aria-hidden="true"><span><i class="legend-building"></i>Oefenen</span><span><i class="legend-fluent"></i>Vlot</span></div>
      <div class="table-grid">${tableRows}</div>
    </section>`;

  document.querySelector<HTMLButtonElement>("#startSession")!.addEventListener("click", startSession);
}

function startSession(): void {
  const snapshot = masterySnapshot();
  activeSession = {
    id: createId("table-session"),
    startedAt: new Date().toISOString(),
    completed: 0,
    firstTryCorrect: 0,
    helpCount: 0,
    recentFactIds: [],
    sessionFactIds: [],
    retryQueue: [],
    startStages: new Map([...snapshot.entries()].map(([id, mastery]) => [id, mastery.currentStage])),
  };
  currentScreen = "PRACTICE";
  presentNextQuestion();
}

function chooseNextFact(): MultiplicationFact {
  if (!activeSession) throw new Error("Er is geen actieve tafelsessie.");
  const dueRetryIndex = activeSession.retryQueue.findIndex(
    (item) => activeSession!.completed >= item.availableAfter
      && !activeSession!.recentFactIds.slice(-3).includes(item.factId),
  );
  if (dueRetryIndex >= 0) {
    const [retry] = activeSession.retryQueue.splice(dueRetryIndex, 1);
    return MULTIPLICATION_CATALOG.factById.get(retry.factId)!;
  }

  return selectNextTableFact({
    attempts: state.attempts,
    recentFactIds: activeSession.recentFactIds,
    sessionFactIds: activeSession.sessionFactIds,
    itemIndex: activeSession.completed,
    now: new Date().toISOString(),
  }).fact;
}

function presentNextQuestion(): void {
  if (!activeSession) return;
  if (activeSession.completed >= SESSION_LENGTH) {
    completeSession(false);
    return;
  }

  const fact = chooseNextFact();
  activeQuestion = {
    presentationId: createId("table-presentation"),
    fact,
    originalPresentedAt: new Date().toISOString(),
    attemptOrdinal: 1,
    entry: "",
    firstInputAt: null,
    editCount: 0,
    supportUsed: false,
    helpRequested: false,
    locked: false,
  };
  renderPractice();
}

function renderPractice(): void {
  if (!activeSession || !activeQuestion) return;
  currentScreen = "PRACTICE";
  const progress = Math.round((activeSession.completed / SESSION_LENGTH) * 100);
  const keys = ["1", "2", "3", "4", "5", "6", "7", "8", "9", "⌫", "0", "✓"];
  app.innerHTML = `
    <section class="card practice-card">
      <div class="practice-top">
        <div>
          <div class="progress-copy">Som ${activeSession.completed + 1} van ${SESSION_LENGTH}</div>
          <div class="progress-track"><div class="progress-fill" style="width:${progress}%"></div></div>
        </div>
        <button class="stop-btn" id="stopSession" type="button">Stoppen</button>
      </div>
      <div class="sum-area">
        <div class="sum">${activeQuestion.fact.left} × ${activeQuestion.fact.right}</div>
        <div class="answer-display is-empty" id="answerDisplay">?</div>
        <div class="feedback" id="feedback">Vul het antwoord in.</div>
        <div id="hintRegion"></div>
      </div>
      <div class="keypad" aria-label="Rekentoetsenbord">
        ${keys.map((key) => `<button class="key${key === "✓" ? " is-submit" : ""}" type="button" data-key="${key}">${key}</button>`).join("")}
      </div>
      <button class="help-btn" id="helpButton" type="button">Ik weet hem even niet</button>
    </section>`;

  document.querySelectorAll<HTMLButtonElement>("[data-key]").forEach((button) => {
    button.addEventListener("click", () => handleKey(button.dataset.key!));
  });
  document.querySelector<HTMLButtonElement>("#helpButton")!.addEventListener("click", requestHelp);
  document.querySelector<HTMLButtonElement>("#stopSession")!.addEventListener("click", () => completeSession(true));
}

function updateAnswerDisplay(): void {
  const display = document.querySelector<HTMLDivElement>("#answerDisplay");
  if (!display || !activeQuestion) return;
  display.textContent = activeQuestion.entry || "?";
  display.classList.toggle("is-empty", activeQuestion.entry.length === 0);
  display.classList.remove("is-wrong", "is-correct");
}

function handleKey(key: string): void {
  if (currentScreen !== "PRACTICE" || !activeQuestion || activeQuestion.locked) return;
  if (key === "✓") {
    submitAnswer();
    return;
  }
  if (key === "⌫") {
    if (activeQuestion.entry.length > 0) {
      activeQuestion.entry = activeQuestion.entry.slice(0, -1);
      activeQuestion.editCount += 1;
      updateAnswerDisplay();
    }
    return;
  }
  if (!/^\d$/.test(key) || activeQuestion.entry.length >= 3) return;
  if (activeQuestion.firstInputAt === null) activeQuestion.firstInputAt = new Date().toISOString();
  activeQuestion.entry = activeQuestion.entry === "0" ? key : `${activeQuestion.entry}${key}`;
  updateAnswerDisplay();
}

function currentAttempt(
  enteredAnswer: number | null,
  correct: boolean | null,
  helpRequested: boolean,
): MultiplicationAttempt {
  if (!activeSession || !activeQuestion) throw new Error("Er is geen actieve opgave.");
  const submittedAt = new Date().toISOString();
  const presentedMs = Date.parse(activeQuestion.originalPresentedAt);
  const firstInputMs = activeQuestion.firstInputAt === null
    ? null
    : Date.parse(activeQuestion.firstInputAt);
  return {
    id: createId("table-attempt"),
    profileId: PROFILE_ID,
    sessionId: activeSession.id,
    presentationId: activeQuestion.presentationId,
    factId: activeQuestion.fact.id,
    attemptOrdinal: activeQuestion.attemptOrdinal,
    presentedAt: activeQuestion.originalPresentedAt,
    firstInputAt: activeQuestion.firstInputAt,
    submittedAt,
    enteredAnswer,
    correct,
    helpRequested,
    supportUsed: activeQuestion.supportUsed || helpRequested,
    editCount: activeQuestion.editCount,
    thinkingTimeMs: firstInputMs === null ? null : Math.max(0, firstInputMs - presentedMs),
    totalTimeMs: Math.max(0, Date.parse(submittedAt) - presentedMs),
  };
}

function scheduleRetry(factId: MultiplicationFactId): void {
  if (!activeSession || activeSession.retryQueue.some((item) => item.factId === factId)) return;
  activeSession.retryQueue.push({ factId, availableAfter: activeSession.completed + 4 });
}

function showHint(): void {
  if (!activeQuestion) return;
  const hint = buildTableHint(activeQuestion.fact);
  const region = document.querySelector<HTMLDivElement>("#hintRegion");
  if (!region) return;
  region.innerHTML = `
    <div class="hint-card">
      <strong>${hint.title}</strong>
      <ol class="hint-steps">${hint.steps.map((step) => `<li>${step}</li>`).join("")}</ol>
    </div>`;
}

function prepareSupportedRetry(): void {
  if (!activeQuestion) return;
  activeQuestion.attemptOrdinal += 1;
  activeQuestion.entry = "";
  activeQuestion.firstInputAt = null;
  activeQuestion.editCount = 0;
  activeQuestion.supportUsed = true;
  activeQuestion.locked = false;
  updateAnswerDisplay();
  document.querySelectorAll<HTMLButtonElement>("[data-key]").forEach((button) => { button.disabled = false; });
}

function submitAnswer(): void {
  if (!activeQuestion || activeQuestion.locked || activeQuestion.entry.length === 0) return;
  const enteredAnswer = Number(activeQuestion.entry);
  const correct = enteredAnswer === activeQuestion.fact.answer;
  const attempt = currentAttempt(enteredAnswer, correct, false);
  appendAttempt(attempt);

  const display = document.querySelector<HTMLDivElement>("#answerDisplay")!;
  const feedback = document.querySelector<HTMLDivElement>("#feedback")!;

  if (!correct) {
    display.classList.add("is-wrong");
    feedback.className = "feedback is-help";
    feedback.textContent = "Nog niet. Bekijk de denkstap en probeer opnieuw.";
    scheduleRetry(activeQuestion.fact.id);
    showHint();
    prepareSupportedRetry();
    return;
  }

  activeQuestion.locked = true;
  display.classList.add("is-correct");
  feedback.className = "feedback is-good";
  feedback.textContent = activeQuestion.attemptOrdinal === 1 ? "Meteen goed!" : "Ja, dat is hem.";
  document.querySelectorAll<HTMLButtonElement>("[data-key], #helpButton").forEach((button) => { button.disabled = true; });
  if (activeQuestion.attemptOrdinal === 1 && activeSession) activeSession.firstTryCorrect += 1;

  const presentationId = activeQuestion.presentationId;
  window.setTimeout(() => finishQuestion(presentationId), 650);
}

function requestHelp(): void {
  if (!activeSession || !activeQuestion || activeQuestion.locked || activeQuestion.supportUsed) return;
  appendAttempt(currentAttempt(null, null, true));
  activeSession.helpCount += 1;
  scheduleRetry(activeQuestion.fact.id);
  const feedback = document.querySelector<HTMLDivElement>("#feedback")!;
  feedback.className = "feedback is-help";
  feedback.textContent = "Geen probleem. Gebruik deze denkstap.";
  showHint();
  prepareSupportedRetry();
  document.querySelector<HTMLButtonElement>("#helpButton")!.disabled = true;
}

function finishQuestion(presentationId: string): void {
  if (
    currentScreen !== "PRACTICE"
    || !activeSession
    || !activeQuestion
    || activeQuestion.presentationId !== presentationId
  ) return;
  activeSession.completed += 1;
  activeSession.recentFactIds.push(activeQuestion.fact.id);
  activeSession.recentFactIds = activeSession.recentFactIds.slice(-6);
  activeSession.sessionFactIds.push(activeQuestion.fact.id);
  presentNextQuestion();
}

function completeSession(stoppedEarly: boolean): void {
  if (!activeSession) {
    renderHome();
    return;
  }
  const completedSession = activeSession;
  state = {
    ...state,
    sessions: [...state.sessions, {
      id: completedSession.id,
      startedAt: completedSession.startedAt,
      endedAt: new Date().toISOString(),
      questionCount: completedSession.completed,
      firstTryCorrect: completedSession.firstTryCorrect,
      helpCount: completedSession.helpCount,
      stoppedEarly,
    }].slice(-100),
  };
  persistState();
  renderSummary(completedSession, stoppedEarly);
}

function renderSummary(session: ActiveSession, stoppedEarly: boolean): void {
  currentScreen = "SUMMARY";
  activeQuestion = null;
  const snapshot = masterySnapshot();
  const newlyFluent = MULTIPLICATION_CATALOG.facts.filter((fact) => {
    const before = session.startStages.get(fact.id) ?? "UNSEEN";
    const after = snapshot.get(fact.id)?.currentStage ?? "UNSEEN";
    return masteryRank(before) < masteryRank("FLUENT") && masteryRank(after) >= masteryRank("FLUENT");
  });
  const accuracy = session.completed === 0
    ? 0
    : Math.round((session.firstTryCorrect / session.completed) * 100);

  app.innerHTML = `
    <section class="card summary-card">
      <div class="summary-icon" aria-hidden="true">★</div>
      <p class="eyebrow">${stoppedEarly ? "Goed gestopt" : "Training klaar"}</p>
      <h1>${stoppedEarly ? "Dit telt ook." : "Sterk geoefend!"}</h1>
      <p class="intro">${stoppedEarly ? "Wat je hebt gedaan is veilig bewaard." : "De volgende training kiest opnieuw wat jij nodig hebt."}</p>
      <div class="stats">
        <div class="stat"><strong>${session.completed}</strong><span>sommen</span></div>
        <div class="stat"><strong>${session.firstTryCorrect}</strong><span>meteen goed</span></div>
        <div class="stat"><strong>${accuracy}%</strong><span>eerste kans</span></div>
      </div>
      ${newlyFluent.length > 0 ? `<div class="new-fluent"><strong>Nieuw vlot:</strong><br />${newlyFluent.map((fact) => `${fact.left} × ${fact.right}`).join(", ")}</div>` : ""}
      <div class="summary-actions">
        <button class="primary-btn" id="againButton" type="button">Nog 15 sommen</button>
        <button class="secondary-btn" id="homeButton" type="button">Bekijk mijn tafels</button>
      </div>
    </section>`;
  document.querySelector<HTMLButtonElement>("#againButton")!.addEventListener("click", startSession);
  document.querySelector<HTMLButtonElement>("#homeButton")!.addEventListener("click", renderHome);
  activeSession = null;
}

document.addEventListener("keydown", (event) => {
  if (currentScreen !== "PRACTICE") return;
  if (/^\d$/.test(event.key)) {
    event.preventDefault();
    handleKey(event.key);
  } else if (event.key === "Backspace") {
    event.preventDefault();
    handleKey("⌫");
  } else if (event.key === "Enter") {
    event.preventDefault();
    handleKey("✓");
  }
});

renderHome();
